/* assets/component-marquee.js
 * Shared marquee engine — measure-fill-resize for a seamless horizontal loop.
 *
 * Promoted from the Slice 12 section-local marquee engine to a shared engine at
 * Slice 38 P1b. Any consumer that emits the data-attribute contract below gets
 * a seamless marquee with zero engine edits — the section/block owns the chrome
 * (styling), the engine owns the mechanics (measure/clone/animate).
 *
 * Engine contract (SLICE-38-P1B-SPEC §1):
 *   [data-marquee]         consumer emits — engine root; JS autodiscovers it.
 *   [data-marquee-track]   consumer emits — overflow-clipping viewport, in root.
 *   [data-marquee-row]     consumer emits — the animated live row (direct child
 *                          of the track); the items are its children.
 *   --mq-speed             consumer emits on root — full-loop duration, format
 *                          <int>s (e.g. 30s). Read by the CSS animation.
 *   --mq-direction-anim    consumer emits on root (optional) — normal | reverse;
 *                          the CSS falls back to normal.
 *   data-marquee-pause     consumer emits on root (optional) — "hover" pauses the
 *                          animation on hover (pure-CSS; see component-marquee.css).
 *   --mq-translate         ENGINE writes on the row — loop distance (one set-
 *                          width), set after measure + clone.
 *   data-marquee-ready     ENGINE writes on the row — gates the CSS animation.
 *   .mq-row-clone          ENGINE-internal — display:contents clone marker.
 *
 * Behaviour (byte-identical to the section-local engine it replaces): the consumer
 * emits ONE live row; `content_for "blocks"` renders each item once — there is
 * no server-side duplication. This engine measures the live row's width,
 * computes how many clones are needed to fill ~2x the container width, then
 * appends that many clones INSIDE the live row. Each clone is display: contents
 * (CSS rule), so it produces no box and its children become flex children of
 * the live row. The live row is the animated element; the clones are display-
 * contents wrappers — clone-as-sibling-of-row would land clone items in the
 * track's block formatting context, breaking the loop.
 *
 * Translation: the CSS keyframe ends in translateX(var(--mq-translate, -50%)).
 * This engine writes --mq-translate = "-<setWidth>px" on the row so the
 * animation loops by exactly one set-width — the seam is always seamless
 * regardless of how many clones were needed to fill the viewport. The -50%
 * fallback keeps the keyframe valid pre-hydration / on JS failure.
 *
 * The CSS animation is gated by a [data-marquee-ready] attribute on the live
 * row, applied here AFTER the first fill completes. Pre-hydration the row sits
 * static (no FOUC); JS failure leaves items visible and static. pause-on-hover
 * and prefers-reduced-motion stay pure-CSS so they survive a script-load failure.
 *
 * Resize handling: a ResizeObserver on the track re-runs the fill whenever the
 * container width changes (window resize, device-preview switch). The observer
 * guards on unchanged width to avoid thrashing. The row stores its observer
 * reference as a defensive guard: no current path re-inits a live row
 * (shopify:section:load replaces the section subtree wholesale — old row and
 * observer detach and GC together), but a future re-init path could rerun
 * initMarquee on a still-attached row, and the stored reference lets that path
 * disconnect the prior observer first. See the matching note at the guard
 * itself in initMarquee.
 *
 * IIFE-dedup-guarded per Rule J12 (window.__BLOCKDEDUP_MARQUEE__).
 *
 * Lifecycle:
 *   - DOMContentLoaded        -> initAll(document)
 *   - shopify:section:load    -> remove stale clones, initAll(event.target)
 *     (customizer hot-reload)
 *   - shopify:section:unload  -> no-op (Shopify removes the section subtree)
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_MARQUEE__) {
    return;
  }
  window.__BLOCKDEDUP_MARQUEE__ = true;

  function stripIdentityAttrs(node) {
    // Walk the clone subtree and strip attributes that would make the clone
    // register as a second editor target or a duplicate DOM id.
    node.removeAttribute('id');
    node.removeAttribute('data-block-id');
    var attrs = Array.prototype.slice.call(node.attributes);
    for (var i = 0; i < attrs.length; i++) {
      if (attrs[i].name.indexOf('data-shopify-editor') === 0) {
        node.removeAttribute(attrs[i].name);
      }
    }
    var children = node.children;
    for (var j = 0; j < children.length; j++) {
      stripIdentityAttrs(children[j]);
    }
  }

  function initMarquee(rootEl) {
    var trackEl = rootEl.querySelector('[data-marquee-track]');
    if (!trackEl) {
      return;
    }
    var rowEl = trackEl.querySelector('[data-marquee-row]');
    if (!rowEl) {
      return;
    }
    // Idempotency: bail if a clone already exists in this track. This guards
    // initMarquee from running twice on the same row and double-attaching the
    // ResizeObserver. It is intentionally distinct from fill()'s own clone-
    // strip at the top of fill(): initMarquee runs once per row (this check
    // enforces it), and fill() is the re-entrant part — called by init and
    // by the ResizeObserver callback, so each fill() must strip the prior
    // fill's clones before re-measuring. The two clone checks serve different
    // lifecycles, not accidental duplication.
    if (trackEl.querySelector('.mq-row-clone')) {
      return;
    }

    var lastContainerWidth = -1;

    function fill() {
      // Strip any existing clones first so scrollWidth measures the live set
      // only (the resize callback also re-enters here after the initial fill).
      var existingClones = rowEl.querySelectorAll('.mq-row-clone');
      for (var i = 0; i < existingClones.length; i++) {
        existingClones[i].parentNode.removeChild(existingClones[i]);
      }

      var setWidth = rowEl.scrollWidth;
      var containerWidth = trackEl.offsetWidth;
      if (setWidth <= 0) {
        return;
      }

      // Generous fill — enough clones to exceed 2x the container width so the
      // animation always sweeps the full viewport regardless of how short the
      // live set is. Floor at 1 to preserve seamless-loop math for the case
      // where the live set is already wider than 2x the container.
      var cloneCount = Math.ceil((2 * containerWidth) / setWidth);
      if (cloneCount < 1) {
        cloneCount = 1;
      }

      var fragment = document.createDocumentFragment();
      for (var c = 0; c < cloneCount; c++) {
        var clone = rowEl.cloneNode(true);
        clone.classList.add('mq-row-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-marquee-row');
        stripIdentityAttrs(clone);
        fragment.appendChild(clone);
      }
      rowEl.appendChild(fragment);

      // Translate distance = exactly one set-width so the loop is seamless.
      // The keyframe reads this via var(--mq-translate, -50%); fallback only
      // kicks in if JS never ran.
      rowEl.style.setProperty('--mq-translate', '-' + setWidth + 'px');
    }

    fill();

    // Readiness gate — CSS keys the animation off [data-marquee-ready] on the
    // live row. Not set on clones: clones are display: contents and are not
    // the animated element. Stays set across resizes — don't toggle it off/on.
    rowEl.setAttribute('data-marquee-ready', '');
    lastContainerWidth = trackEl.offsetWidth;

    // Disconnect any leftover observer from a prior init on this row before
    // attaching a new one — defensive guard against the same rowEl node being
    // re-initialized. No current call path triggers it: the top-of-initMarquee
    // idempotency check returns early when this rowEl already has clones, and
    // shopify:section:load replaces the section subtree wholesale (old rowEl +
    // its observer detach together and GC-collect as an isolated cycle). This
    // is NOT a leak guard for shopify:section:load; it is cheap insurance for
    // a future re-init path (e.g., behavior-toggle live-rebuild) that could
    // legitimately rerun initMarquee on a still-attached row.
    if (rowEl.__mqResizeObserver) {
      rowEl.__mqResizeObserver.disconnect();
      rowEl.__mqResizeObserver = null;
    }
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        var w = trackEl.offsetWidth;
        if (w === lastContainerWidth) {
          return;
        }
        fill();
        lastContainerWidth = w;
      });
      ro.observe(trackEl);
      rowEl.__mqResizeObserver = ro;
    }
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var marquees = root.querySelectorAll('[data-marquee]');
    for (var i = 0; i < marquees.length; i++) {
      initMarquee(marquees[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) {
      return;
    }
    // Remove any stale clones in the rebuilt section first.
    var staleClones = event.target.querySelectorAll('.mq-row-clone');
    for (var i = 0; i < staleClones.length; i++) {
      staleClones[i].parentNode.removeChild(staleClones[i]);
    }
    initAll(event.target);
  });
})();
